import { useEffect, useState } from "react";
import { MapPin, Pencil } from "lucide-react";
import { getCategories, apiFetch } from "@/lib/api";
import { useWeather } from "@/lib/weather";
import { CategoryChips } from "@/components/CategoryChips";
import { NewsCard } from "@/components/NewsCard";
import { WeatherAlert } from "@/components/WeatherAlert";
import { WeatherIcon } from "@/components/WeatherIcon";
import { CitySearch } from "@/components/CitySearch";

const deg = (t) => (t == null ? "–" : `${Math.round(t)}°`);
const dayName = (iso, i) => (i === 0 ? "Oggi" : i === 1 ? "Domani"
  : new Date(`${iso}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric" }));
const MAPS = [
  { key: "rain", label: "Pioggia" },
  { key: "temp", label: "Temperatura" },
  { key: "wind", label: "Vento" },
  { key: "clouds", label: "Nuvole" },
];

function NowCard({ city, data, onChange }) {
  return (
    <div data-testid="meteo-now" className="rounded-2xl bg-brand text-onBrand p-6 sm:p-8 flex flex-col sm:flex-row sm:items-end gap-6 justify-between">
      <div>
        <button type="button" data-testid="meteo-change-city" onClick={onChange} className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.16em] opacity-90 hover:opacity-100">
          <MapPin size={13} /> {city.name} <Pencil size={12} className="ml-1" />
        </button>
        <div className="flex items-center gap-4 mt-3">
          <WeatherIcon symbol={data.now.symbol} size={56} />
          <span data-testid="meteo-now-temp" className="font-serif text-6xl font-semibold tabular-nums">{deg(data.now.temp)}</span>
        </div>
        <p className="mt-2 text-lg font-medium">{data.now.label}</p>
      </div>
      <p data-testid="meteo-today-minmax" className="font-mono text-sm tabular-nums">min {deg(data.today.min)} · max {deg(data.today.max)}</p>
    </div>
  );
}

function Hourly({ hours }) {
  return (
    <section data-testid="meteo-hourly">
      <h2 className="font-serif text-lg font-semibold text-onSurface mb-3">Prossime ore</h2>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        {hours.map((h, i) => (
          <div key={i} className="shrink-0 w-16 rounded-xl border border-hair bg-surfaceSecondary py-3 flex flex-col items-center gap-1.5">
            <span className="font-mono text-[11px] text-subtle">{h.time}</span>
            <WeatherIcon symbol={h.symbol} size={20} className="text-brand" />
            <span className="text-sm font-bold tabular-nums text-onSurface">{deg(h.temp)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Daily({ days }) {
  return (
    <section data-testid="meteo-daily">
      <h2 className="font-serif text-lg font-semibold text-onSurface mb-3">I prossimi giorni</h2>
      <ul className="divide-y divide-hair border border-hair rounded-2xl overflow-hidden">
        {days.map((d, i) => (
          <li key={d.date} data-testid={`meteo-day-${i}`} className="flex items-center gap-3 px-4 py-3 bg-surface">
            <span className="w-32 text-sm font-semibold text-onSurface capitalize">{dayName(d.date, i)}</span>
            <WeatherIcon symbol={d.symbol} size={18} className="text-brand" />
            <span className="flex-1 text-sm text-subtle truncate">{d.label}</span>
            <span className="font-mono text-sm tabular-nums text-onSurface">{deg(d.min)} / {deg(d.max)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function WeatherNews() {
  const [items, setItems] = useState(null);
  useEffect(() => { apiFetch(`/meteo/notizie`, { auth: false }).then((r) => setItems(r.articles || [])).catch(() => setItems([])); }, []);
  if (!items?.length) return null;
  return (
    <section data-testid="meteo-news">
      <h2 className="font-serif text-lg font-semibold text-onSurface mb-3">Maltempo e allerte</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((a) => <NewsCard key={a.article_id} article={a} />)}
      </div>
    </section>
  );
}

function Maps({ city }) {
  const [layer, setLayer] = useState("rain");
  const src = `https://embed.windy.com/embed2.html?lat=${city.lat}&lon=${city.lon}&detailLat=${city.lat}&detailLon=${city.lon}`
    + `&zoom=7&level=surface&overlay=${layer}&product=ecmwf&menu=&message=true&marker=true&calendar=now&type=map&location=coordinates&metricWind=km%2Fh&metricTemp=%C2%B0C`;
  return (
    <section data-testid="meteo-maps">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="font-serif text-lg font-semibold text-onSurface mr-2">Cartine</h2>
        {MAPS.map((m) => (
          <button key={m.key} type="button" data-testid={`meteo-map-${m.key}`} onClick={() => setLayer(m.key)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${layer === m.key ? "bg-brand text-onBrand border-brand" : "bg-surfaceSecondary text-onSurfaceSecondary border-hair hover:text-onSurface"}`}>
            {m.label}
          </button>
        ))}
      </div>
      <iframe title="Cartina meteo" data-testid="meteo-map-frame" src={src} loading="lazy" className="w-full h-[380px] sm:h-[480px] rounded-2xl border border-hair" />
    </section>
  );
}

export default function Meteo() {
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(false);
  const { city, data } = useWeather();
  useEffect(() => { getCategories().then((r) => setCategories(r.categories || [])).catch(() => {}); }, []);
  useEffect(() => { document.title = city ? `Meteo ${city.name} – Readoo` : "Meteo – Readoo"; }, [city]);

  return (
    <div>
      <CategoryChips categories={categories} active="Meteo" />
      <div data-testid="meteo-page" className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-onSurface">Meteo</h1>
        <WeatherAlert />
        {(editing || !city) && <CitySearch onDone={() => setEditing(false)} />}
        {city && data ? (
          <>
            <NowCard city={city} data={data} onChange={() => setEditing((v) => !v)} />
            <Hourly hours={data.hourly} />
            <Daily days={data.daily} />
            <WeatherNews />
            <Maps city={city} />
            <p className="font-mono text-[11px] text-subtle">{data.credits} · Cartine: Windy · Città: © OpenStreetMap</p>
          </>
        ) : city && <p data-testid="meteo-loading" className="text-subtle">Carico il meteo…</p>}
      </div>
    </div>
  );
}
